// This custom table is used in the my courses , subjects, chapters, topics, subtopics.
import React, { useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Tooltip,
  useTheme,
  Box,
  Text,
  Flex,
} from "@chakra-ui/react";
import { TbEdit } from "react-icons/tb";
import { MdOutlineDeleteOutline } from "react-icons/md";
import DeletePopup from "../../popups/DeletePopup";
import ChangeStatusPopup from "../../popups/ChangeStatusPopup";
import { updateSubject } from "../../../store/slice/subjectSlice";
import { updateTopic } from "../../../store/slice/topicSlice";
import { updateCourse } from "../../../store/slice/courseSlice";
import { updateChapter } from "../../../store/slice/chapterSlice";
import { useDispatch } from "react-redux";
import { deleteCourse } from "../../../store/slice/courseSlice";
import { deleteSubject } from "../../../store/slice/subjectSlice";
import { deleteChapter } from "../../../store/slice/chapterSlice";
import { deleteTopic } from "../../../store/slice/topicSlice";
import {
  deletepackage,
  updatepackage,
} from "../../../store/slice/packageSlice";
import { useToastContext } from "../../toastNotificationProvider/ToastNotificationProvider";
const CustomTable = ({
  data,
  extraColumns = [],
  columnOrder = [],
  onEdit,
  entityType,
  limit,
}) => {
  const [isDeletePopupOpen, setDeletePopupOpen] = useState(false);
  const [isStatusPopupOpen, setStatusPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const theme = useTheme();
  const dispatch = useDispatch();
  const { addNotification } = useToastContext();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = limit;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = data.slice(startIndex, endIndex);
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const {
    editButtonColor,
    deleteBtnColor,
    activeStatusColor,
    inactiveStatusColor,
    inactiveButtonTextColor,
    activeButtonTextColor,
  } = theme.colors.pallete;

  const fixedColumns = [
    { label: "S.No", key: "sno" },
    { label: "Title", key: "titleOrName" },
    { label: "Description", key: "description" },
    { label: "Display Order", key: "displayOrder" },
    { label: "Status", key: "status" },
  ];

  let allColumns = [...fixedColumns, ...extraColumns];

  if (columnOrder.length > 0) {
    allColumns = columnOrder
      .map((key) => allColumns.find((col) => col.key === key))
      .filter(Boolean);
  }

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setDeletePopupOpen(true);
  };

  const handleStatusClick = (row) => {
    setSelectedRow(row);
    setStatusPopupOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedRow) {
      const updatedStatus = !selectedRow.isActive;
      try {
        if (entityType === "course") {
          await dispatch(
            updateCourse({
              id: selectedRow.id,
              formData: { isActive: updatedStatus },
            })
          );
        } else if (entityType === "subject") {
          await dispatch(
            updateSubject({
              id: selectedRow.id,
              formData: { isActive: updatedStatus },
            })
          );
        } else if (entityType === "chapter") {
          await dispatch(
            updateChapter({
              id: selectedRow.id,
              formData: { isActive: updatedStatus },
            })
          );
        } else if (entityType === "topic") {
          await dispatch(
            updateTopic({
              id: selectedRow.id,
              formData: { isActive: updatedStatus },
            })
          );
        } else if (entityType === "package") {
          await dispatch(
            updatepackage({
              id: selectedRow.id,
              formData: { isActive: updatedStatus },
            })
          );
        }

        setStatusPopupOpen(false);
        addNotification(
          `${
            entityType.charAt(0).toUpperCase() + entityType.slice(1)
          } status updated`,
          "success",
          3000
        );
      } catch (err) {
        console.error("Failed to update status", err);
        addNotification("Something went wrong!", "error", 3000);
      }
    }
  };

  const getColumnValue = (row, key, index) => {
    if (key === "sno") {
      return startIndex + index + 1;
    }
    if (key === "titleOrName") {
      return row.title || row.name || "—";
    }
    if (key === "description") {
      return (
        <Tooltip label={row[key]} hasArrow>
          <Text noOfLines={2}>{row[key] || "—"}</Text>
        </Tooltip>
      );
    }

    if (key === "subject") {
      return row.subjectName || "—";
    }

    if (key === "chapter") {
      return row.chapterName || "—";
    }
    if (key === "status") {
      return (
        <Button
          w={"100px"}
          h={"30px"}
          bg={row.isActive ? activeStatusColor : inactiveStatusColor}
          color={row.isActive ? activeButtonTextColor : inactiveButtonTextColor}
          _hover={{ opacity: 0.8 }}
          onClick={() => handleStatusClick(row)}
          fontSize="14px"
          fontWeight="400"
          borderRadius={"6px"}
        >
          {row.isActive ? "Active" : "Inactive"}
        </Button>
      );
    }
    return row[key] || "—";
  };

  const handleConfirmDelete = async () => {
    if (selectedRow) {
      try {
        if (entityType === "course") {
          await dispatch(deleteCourse(selectedRow.id));
        } else if (entityType === "subject") {
          await dispatch(deleteSubject(selectedRow.id));
        } else if (entityType === "chapter") {
          await dispatch(deleteChapter(selectedRow.id));
        } else if (entityType === "topic") {
          await dispatch(deleteTopic(selectedRow.id));
        } else if (entityType === "package") {
          await dispatch(deletepackage(selectedRow.id));
        }
        setDeletePopupOpen(false);
        addNotification(
          `${
            entityType.charAt(0).toUpperCase() + entityType.slice(1)
          } deleted successfully`,
          "success",
          3000
        );
      } catch (error) {
        console.error("Error deleting:", error);
      }
    }
  };

  return (
    <Box overflowX="auto" mt={6}>
      <Table variant="unstyled">
        <Thead>
          <Tr>
            {allColumns.map((col, index) => (
              <Th whiteSpace="nowrap" key={index}>
                {col.label}
              </Th>
            ))}
            <Th>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          {Array.isArray(data) &&
            paginatedData.map((row, rowIndex) => (
              <Tr key={rowIndex}>
                {allColumns.map((col, colIndex) => (
                  <Td
                    fontSize={"14px"}
                    key={colIndex}
                    maxW={col.key === "description" ? "300px" : "200px"}
                  >
                    {getColumnValue(row, col.key, rowIndex)}
                  </Td>
                ))}

                <Td>
                  <Flex>
                    <Button
                      color={editButtonColor}
                      p={0}
                      onClick={() => onEdit(row.id)}
                      variant="ghost"
                    >
                      <TbEdit size={"20px"} />
                    </Button>
                    <Button
                      p={0}
                      color={deleteBtnColor}
                      onClick={() => handleDeleteClick(row)}
                      variant="ghost"
                    >
                      <MdOutlineDeleteOutline size={"20px"} />
                    </Button>
                  </Flex>
                </Td>
              </Tr>
            ))}
        </Tbody>
      </Table>

      <Flex justify="flex-end" align="center" m={10} gap={4}>
        <Button
          size="sm"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          isDisabled={currentPage === 1}
        >
          Prev
        </Button>

        <Text fontSize="sm" fontWeight="medium">
          {currentPage} / {totalPages}
        </Text>

        <Button
          size="sm"
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </Flex>

      <DeletePopup
        isOpen={isDeletePopupOpen}
        onClose={() => setDeletePopupOpen(false)}
        onDelete={handleConfirmDelete}
        text={`the ${entityType}`}
      />

      <ChangeStatusPopup
        isOpen={isStatusPopupOpen}
        onClose={() => setStatusPopupOpen(false)}
        onClickStatusChange={handleConfirmStatusChange}
        text={` ${entityType}`}
      />
    </Box>
  );
};

export default CustomTable;
